import { Service } from "../../../glue/Kernel.js";
import { createConsolePanel, createLobby } from "./ui.js";
import { SOLID_MATERIALS } from "../../../engine/tiles.js";

// The chrome around the game: the join panel (name / colour / server / volume), the
// developer console + chat feed, the room lobby, and the join gesture itself. It also
// owns the overlay and status line, and turns the connection's `net.*` events into
// printed lines and feed messages. RenderService and InputService reach it for the
// console (isTyping / open) and the chat feed (chatFeed / ageChat / feed).

const NAME_KEY = "memewars.name";
const COLOR_KEY = "memewars.color";
const NAME_ADJ = ["Dank", "Spicy", "Based", "Cursed", "Feral", "Turbo", "Rad", "Yeet", "Sus", "Big", "Lil", "Epic"];
const NAME_NOUN = ["Doge", "Stonks", "Nyan", "Troll", "Pepe", "Chad", "Shrek", "Cheems", "Gigachad", "Wojak", "Blob", "Goose"];
const pick = (a) => a[Math.floor(Math.random() * a.length)];
const randomName = () => (pick(NAME_ADJ) + pick(NAME_NOUN)).slice(0, 16);
const readStored = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const writeStored = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode: never mind */ } };

// A DOM KeyboardEvent.code -> a short human label for the keybind menu ("KeyW" -> "W", arrows -> ↑↓←→).
function keyLabel(code) {
    if (!code) return "—";
    const map = { ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→", Backquote: "`", Escape: "Esc", WheelUp: "Scroll ↑", WheelDown: "Scroll ↓" };
    return map[code] || code.replace(/^Key/, "").replace(/^Digit/, "");
}

export class UIService extends Service {
    on_phase_consolidate() {
        this.net = this.serviceAPI.require("net");
        this.audio = this.serviceAPI.require("audio");
        const client = this.net.client;

        // --- DOM ---
        this.overlay = document.getElementById("overlay");
        this.statusEl = document.getElementById("status");
        this.nameInput = document.getElementById("name");
        this.colorInput = document.getElementById("color");

        // Name: remember the last one, or roll a random meme handle.
        this.nameInput.value = readStored(NAME_KEY) || randomName();

        // Colour: remember the last picked one; if you never pick one, the server assigns
        // a colour far from the ones already in play. `colorChosen` tracks whether it's
        // yours.
        this.colorChosen = false;
        const storedColor = readStored(COLOR_KEY);
        if (storedColor) { this.colorInput.value = storedColor; this.colorChosen = true; }
        this.colorInput.addEventListener("input", () => { this.colorChosen = true; });

        // Volume sliders — the lobby's and the in-game settings' (Esc). Both drive the
        // same audio buses and stay in sync with each other and with the console's
        // /volume, so wherever you change it, every slider reflects it.
        this.settingsEl = document.getElementById("settings");
        this._wireVolume("volBgm", "volSfx");
        this._wireVolume("sVolBgm", "sVolSfx");
        document.getElementById("sClose")?.addEventListener("click", () => this.closeSettings());
        // Keybinds (Set 12.2): the list is (re)built each time settings opens; Reset clears overrides.
        this.kbListEl = document.getElementById("keybindList");
        document.getElementById("kbReset")?.addEventListener("click", () => { this.serviceAPI.require("input").resetBinds(); this.renderKeybinds(); });
        // Settings tabs (Set 12.6): Audio / Controls. Reuses the wizard's nav chrome; scoped to #settings.
        for (const t of document.querySelectorAll("#settings .wiz-navitem")) t.addEventListener("click", () => {
            for (const x of document.querySelectorAll("#settings .wiz-navitem")) x.classList.toggle("on", x === t);
            for (const p of document.querySelectorAll("#settings .set-pane")) p.classList.toggle("hidden", p.dataset.setpane !== t.dataset.settab);
        });

        // Block inventory (I) and the trowel's block wheel (C) — both purely presentation,
        // fed from the snapshot's `blocks`. The wheel state lives here; RenderService draws
        // it and InputService drives the pointer.
        this.inventoryEl = document.getElementById("inventory");
        this.invListEl = document.getElementById("invList");
        this._invSig = null;
        this.wheelOpen = false;
        this.wheelSel = null; // material the pointer is over, or null
        this.picker = null; // the ONE composable radial picker (Set 21.5): { options, sel(index), resolve }, or null
        this.menuOpen = null; // an open plugin template menu (its {id, spec}), or null (UI framework D)
        this.menuSel = -1;    // hovered option index, or -1

        // --- console + chat feed ---
        const panel = createConsolePanel({
            send: (msg) => client.send(msg),
            onName: (n) => { this.nameInput.value = n; },
            onColor: (c) => { this.colorInput.value = c; this.colorChosen = true; },
            onConnect: (host) => { document.getElementById("server").value = host; this.join(); },
            onVolume: (bus, n) => this.audio.volume(bus, n),
            onGain: (token, n) => this.audio.gain(token, n),
        });
        this.con = panel.con;
        this.feed = panel.feed;
        this.ageChat = panel.ageChat;
        this.chatFeed = panel.chatFeed;

        // --- how the connection shows up on screen ---
        // The Client has already updated its own state by the time these fire, so they
        // only touch the screen. Listeners get (name, matches, ...args) — the payload is
        // the last argument (EmitterFeature convention). net.state (the fx stream) is
        // RenderService's, not ours.
        this.net.on("net.connecting", () => { this.statusEl.textContent = "connecting…"; });
        this.net.on("net.welcome", (_name, _m, m) => {
            this.overlay.classList.add("hidden");
            this.statusEl.textContent = "";
            this.con.print(`entered ${m.room.name} — ${m.room.modeLabel}`, "system");
            const lim = [];
            if (m.room.fragLimit) lim.push(`${m.room.fragLimit} frags`);
            if (m.room.timeLimit) lim.push(`${Math.round(m.room.timeLimit / 60)} min`);
            if (lim.length) this.con.print(`  limit: ${lim.join(" or ")}`, "echo");
        });
        this.net.on("net.chat", (_name, _m, m) => this.onChat(m));
        this.net.on("net.notice", (_name, _m, m) => {
            this.con.print(m.text, m.kind === "error" ? "error" : "info");
            if (m.kind === "error") this.feed(m.text, "error");
        });
        this.net.on("net.joinfail", (_name, _m, m) => {
            this.statusEl.textContent = m.error;
            this.overlay.classList.remove("hidden");
            this.con.print(m.error, "error");
        });
        this.net.on("net.close", () => {
            this.statusEl.textContent = "disconnected";
            this.overlay.classList.remove("hidden");
        });
        this.net.on("net.error", (_name, _m, msg) => { this.statusEl.textContent = msg; });

        createLobby({ client, join: () => this.join() });
    }

    // Are we clear of the join panel? (Input asks, to know whether the game keys are live.)
    overlayHidden() { return this.overlay.classList.contains("hidden"); }

    // Wire a Music/Sound slider pair to the audio buses. Registered so _syncVolume keeps
    // every pair (lobby + settings) showing the current value. Dragging is a user gesture,
    // so touching Music also unlocks + starts the (browser-blocked-until-now) audio.
    _wireVolume(bgmId, sfxId) {
        const bgm = document.getElementById(bgmId), sfx = document.getElementById(sfxId);
        if (!bgm || !sfx) return;
        (this._volSliders ||= []).push({ bgm, sfx });
        bgm.value = this.audio.volume("bgm");
        sfx.value = this.audio.volume("sfx");
        bgm.addEventListener("input", () => { this.audio.volume("bgm", +bgm.value); this.audio.resume(); this.audio.startBgm(); this._syncVolume(); });
        sfx.addEventListener("input", () => { this.audio.volume("sfx", +sfx.value); this._syncVolume(); });
    }

    _syncVolume() {
        const b = this.audio.volume("bgm"), s = this.audio.volume("sfx");
        for (const v of this._volSliders || []) { v.bgm.value = b; v.sfx.value = s; }
    }

    // The in-game settings overlay (Esc). It doesn't pause — the fight runs behind the dim.
    // Build the keybind list in the settings overlay: one row per rebindable action (core + this
    // room's plugin controls/menus). Click a key to rebind it (the next press sets it); a key bound to
    // two+ actions turns the row RED and names the clash. Human-readable key from the DOM code.
    renderKeybinds() {
        if (!this.kbListEl) return;
        const input = this.serviceAPI.require("input");
        const conflicts = input.bindConflicts();
        this.kbListEl.textContent = "";
        for (const b of input.bindables()) {
            const row = document.createElement("div");
            row.className = "kb-row" + (conflicts[b.key] ? " kb-conflict" : "");
            // label + a short description of what the control DOES (Set 12.6), then the rebind key.
            const info = document.createElement("div"); info.className = "kb-info";
            const label = document.createElement("span"); label.className = "kb-label"; label.textContent = b.label;
            info.append(label);
            if (b.desc) { const d = document.createElement("span"); d.className = "kb-desc"; d.textContent = b.desc; info.append(d); }
            const btn = document.createElement("button"); btn.type = "button"; btn.className = "kb-key"; btn.textContent = keyLabel(b.key);
            btn.addEventListener("click", () => { btn.textContent = "press a key…"; input.beginRebind(b.id, () => this.renderKeybinds()); });
            row.append(info, btn);
            if (conflicts[b.key]) { const w = document.createElement("span"); w.className = "kb-warn"; w.textContent = "also: " + conflicts[b.key].filter((l) => l !== b.label).join(", "); row.append(w); }
            this.kbListEl.append(row);
        }
    }

    toggleSettings() {
        if (!this.settingsEl) return;
        if (this.settingsOpen()) this.settingsEl.classList.add("hidden");
        else { this._syncVolume(); this.renderKeybinds(); this.settingsEl.classList.remove("hidden"); } // re-sync + rebuild the keybind list
    }
    closeSettings() { this.settingsEl?.classList.add("hidden"); }
    settingsOpen() { return !!this.settingsEl && !this.settingsEl.classList.contains("hidden"); }

    // The block inventory (I). Doesn't pause. Its rows are rebuilt from the snapshot only
    // when the stock actually changes (updateInventory), so mining/building shows live
    // without thrashing the DOM every frame.
    toggleInventory() {
        if (!this.inventoryEl) return;
        this.inventoryEl.classList.toggle("hidden");
        if (this.inventoryOpen()) this._invSig = null; // force a rebuild on open
    }
    inventoryOpen() { return !!this.inventoryEl && !this.inventoryEl.classList.contains("hidden"); }
    updateInventory(inv) {
        if (!this.inventoryOpen() || !this.invListEl) return;
        const map = inv || {};   // materials are ordinary inventory items now (Set 14)
        const sig = SOLID_MATERIALS.map((m) => map[m.name] || 0).join(",");
        if (sig === this._invSig) return;
        this._invSig = sig;
        this.invListEl.replaceChildren(...SOLID_MATERIALS.map((m) => {
            const n = map[m.name] || 0;
            const row = document.createElement("div");
            row.className = "inv-row" + (n ? "" : " empty");
            const sw = document.createElement("span"); sw.className = "inv-sw"; sw.style.background = m.fill;
            const nm = document.createElement("span"); nm.textContent = m.name;
            const cn = document.createElement("span"); cn.className = "inv-n"; cn.textContent = n;
            row.append(sw, nm, cn);
            return row;
        }));
    }

    // The trowel's block wheel (C). Also doesn't pause; the fight runs behind it.
    openWheel() { this.wheelOpen = true; this.wheelSel = null; }
    closeWheel() { this.wheelOpen = false; this.wheelSel = null; }
    wheelIsOpen() { return this.wheelOpen; }

    // The composable radial picker (Set 21.5): a plugin's client.lua ui.wheel({options, on_pick}) opens
    // this with an arbitrary option list + a resolver. Picking (or cancelling) closes it and resolves with
    // the chosen option's value (or nil). Doesn't pause. One at a time — a fresh open cancels any pending.
    // This is the ONE radial the item wheel, the trowel block wheel, and template menus all fold onto.
    openPicker(options, resolve) {
        if (this.picker && this.picker.resolve) this.picker.resolve(null);
        this.picker = { options: options || [], resolve, sel: -1 };
    }
    closePicker(pick) { const p = this.picker; this.picker = null; if (p && p.resolve) p.resolve(pick ?? null); }
    pickerIsOpen() { return !!this.picker; }

    // A plugin's template menu (UI framework D) — a radial opened by the menu's key. Like the wheel,
    // it doesn't pause; the fight runs behind it.
    openMenu(menu) { this.menuOpen = menu; this.menuSel = -1; }
    closeMenu() { this.menuOpen = null; this.menuSel = -1; }
    menuIsOpen() { return !!this.menuOpen; }

    join() {
        // Blank name is sent as "" so the server can default it to the player's colour.
        const name = this.nameInput.value.trim();
        const server = document.getElementById("server").value || "";
        if (name) writeStored(NAME_KEY, name); // remember it for next time
        // A colour you picked is sent (and remembered); otherwise ask the server to choose.
        let color = "random";
        if (this.colorChosen) { color = this.colorInput.value; writeStored(COLOR_KEY, color); }
        // Joining is a real user gesture, so it's our chance to unlock audio and start the
        // music (browsers block sound until then).
        this.audio.resume();
        this.audio.startBgm();
        this.net.connect(name, color, server);
    }

    // Turn a chat message into a feed line.
    onChat(m) {
        if (!m.from) return this.feed(m.text, "system");         // joins/parts/renames
        // Targeted chat (/to): a whisper to one player, or a server-wide shout.
        if (m.scope === "dm") return this.feed(`[dm] ${m.to ? "→ " + m.to : m.from}: ${m.text}`, "team");
        if (m.scope === "server") return this.feed(`[server] ${m.from}: ${m.text}`, "system");
        const tag = m.team ? `[${m.team}] ` : "";
        this.feed(`${tag}${m.from}: ${m.text}`, m.team ? "team" : "chat");
    }
}
